/**
 * Наблюдения на главной: то, что приложение заметило само.
 *
 * Вторая половина советника. Первая отвечает на вопрос человека; здесь
 * приложение само говорит, что случилось что-то, требующее решения. Раньше
 * про дефицит или несобранный платёж человек узнавал, только если открывал
 * главную и складывал числа глазами.
 *
 * Что здесь намеренно **не** происходит: модель не вызывается. Повод считает
 * арифметика, и он бесплатен для всех — предупреждение о нехватке денег не
 * может быть платной возможностью. Разговор о поводе начинается, только когда
 * человек сам нажмёт, и только там, где диалог ему открыт.
 *
 * «Не сейчас» — полноправный ответ: наблюдение уходит и возвращается лишь
 * тогда, когда изменилась его суть. Иначе это напоминалка, которую перестают
 * читать на второй неделе.
 */
import { useEffect } from 'react';
import type { InsightCandidate } from '@skoka/core';
import { dismissInsight, markInsightShown } from './store.js';
import { Tip } from './tip.js';

export function InsightCards({ insights, onAct, onDismissed }: {
  insights: readonly InsightCandidate[];
  /** Действие наблюдения: экран или разговор — решает главная. */
  onAct: (insight: InsightCandidate) => void;
  /** Отложено: журнал изменился, картину надо перечитать. */
  onDismissed: () => void;
}) {
  // Показ записывается один раз на подпись: по нему видно, что человек это
  // уже видел, и повторный показ того же не считается новым поводом.
  useEffect(() => {
    void (async () => {
      for (const c of insights) await markInsightShown(c);
    })();
  }, [insights.map((c) => `${c.key}:${c.signature}`).join('|')]);

  if (insights.length === 0) return null;

  return (
    <section className={`blk${insights.some((c) => c.severity === 'urgent') ? ' alarm' : ''}`}>
      <div className="blkhead"><b>Заметил
        <Tip>Это расчёт по твоим данным, а не совет модели: он одинаковый на любом
          тарифе. «Не сейчас» убирает наблюдение до тех пор, пока не изменится
          сумма, срок или статус.</Tip></b></div>
      {insights.map((c) => (
        <div className="li acts" key={c.key}>
          <div className="nm">
            <b>{c.text}</b>
            {c.severity === 'urgent' && <span>Это стоит решить сейчас</span>}
          </div>
          {c.action && (
            <button className="btn inline pri" onClick={() => onAct(c)}>{c.action.label}</button>
          )}
          <button className="btn inline" onClick={async () => {
            await dismissInsight(c);
            onDismissed();
          }}>Не сейчас</button>
        </div>
      ))}
    </section>
  );
}
